import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import {
  MusicRec,
  musicService,
} from '../../../../services/musicInfo';
import { tasksService } from '../../../../services/tasks';
import { mapTaskIdToKey } from '../../../../utils/taskKey';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('MusicData');

type RouteParams = {
  musicId?: number;
  taskId?: number;
  targetUserId?: string;
  viewOnly?: boolean;
};

type MusicDataScreenRouteProp = RouteProp<RootStackParamList, 'MusicData'>;

type InfoItemProps = {
  label: string;
  value: string | boolean | undefined;
};

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => {
  let displayValue = '';

  if (typeof value === 'boolean') {
    displayValue = value ? 'Yes' : 'No';
  } else if (typeof value === 'string') {
    displayValue = value || 'Not specified';
  } else {
    displayValue = 'Not specified';
  }

  return (
    <View style={styles.infoItem}>
      <Text style={styles.label}>{label}</Text>
      <Text style={styles.value}>{displayValue}</Text>
      <View style={styles.separator} />
    </View>
  );
};

export const MusicDataScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<MusicDataScreenRouteProp>();
  const [musicInfo, setMusicInfo] = useState<MusicRec | null>(null);
  const [loading, setLoading] = useState(true);

  const musicId = route.params?.musicId;
  const targetUserId = route.params?.targetUserId;
  const viewOnly = route.params?.viewOnly || false;

  useEffect(() => {
    loadMusicInfo();
  }, []);

  const loadMusicInfo = async () => {
    try {
      if (musicId) {
        const info = await musicService.getMusicById(musicId, targetUserId);
        setMusicInfo(info);
      } else {
        // Load the first/latest entry if no specific ID provided
        const allInfo = await musicService.getAllMusic(targetUserId);
        if (allInfo.length > 0) {
          setMusicInfo(allInfo[0]);
        }
      }
    } catch (error) {
      logger.error('Error loading music info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (musicInfo) {
      // Map the data from musicInfo to the format expected by MusicInputScreen
      const mappedData = {
        id: musicInfo.id,
        preludeMusic: musicInfo.preludeMusic,
        processionalMusic: musicInfo.processionalMusic,
        hymnsSongs: musicInfo.hymnsSongs,
        specialMusic: musicInfo.specialMusic,
        recessionalMusic: musicInfo.recessionalMusic,
        favoriteSongs: musicInfo.favoriteSongs,
        liveMusicians: musicInfo.liveMusicians,
        recordedMusic: musicInfo.recordedMusic,
        avoidMusic: musicInfo.avoidMusic,
        culturalMusic: musicInfo.culturalMusic,
        specialRequests: musicInfo.specialRequests,
        notes: musicInfo.notes,
      };

      navigation.navigate('MusicInput', {
        musicData: mappedData,
        isEditing: true
      });
    } else {
      navigation.navigate('MusicInput');
    }
  };

  const handleDelete = () => {
    if (musicInfo) {
      Alert.alert(
        'Delete Music Preferences',
        'Are you sure you want to delete this music preferences? This action cannot be undone.',
        [
          {
            text: 'Cancel',
            style: 'cancel',
          },
          {
            text: 'Delete',
            style: 'destructive',
            onPress: async () => {
              try {
                await musicService.deleteMusic(musicInfo.id);

                // CRITICAL: Check if this was the last entry and reset task if so
                const remainingData = await musicService.getAllMusic();
                if (!remainingData || remainingData.length === 0) {
                  const taskKey = mapTaskIdToKey(74); // Music task ID
                  await tasksService.resetTask(taskKey);
                }

                navigation.navigate('FinalWishesChecklist');
              } catch (error) {
                logger.error('Error deleting music info:', error);
                Alert.alert('Error', 'Failed to delete music preferences');
              }
            },
          },
        ]
      );
    }
  };

  const handleBack = () => {
    navigation.navigate('FinalWishesChecklist');
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>
        <Text style={styles.title}>Music Preferences Details</Text>
        <View style={styles.whiteContainer}>
          <View style={styles.loadingContainer}>
            <Text style={styles.loadingText}>Loading...</Text>
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <TouchableOpacity style={styles.notificationButton}>
          <Image
            source={require('../../../../assets/icons/notification.png')}
            style={styles.notificationIcon}
          />
        </TouchableOpacity>
      </View>

      <Text style={styles.title}>Music Preferences Details</Text>
      <View style={styles.whiteContainer}>
        {viewOnly && (
          <View style={styles.viewOnlyBanner}>
            <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
          </View>
        )}
        <ScrollView style={styles.scrollView}>
          {musicInfo ? (
            <>
              {/* Display all relevant fields using InfoItem components */}
              {musicInfo.preludeMusic && (
                <InfoItem label="Prelude Music" value={musicInfo.preludeMusic} />
              )}
              {musicInfo.processionalMusic && (
                <InfoItem label="Processional Music" value={musicInfo.processionalMusic} />
              )}
              {musicInfo.hymnsSongs && (
                <InfoItem label="Hymns & Songs" value={musicInfo.hymnsSongs} />
              )}
              {musicInfo.specialMusic && (
                <InfoItem label="Special Music" value={musicInfo.specialMusic} />
              )}
              {musicInfo.recessionalMusic && (
                <InfoItem label="Recessional Music" value={musicInfo.recessionalMusic} />
              )}
              {musicInfo.favoriteSongs && (
                <InfoItem label="Favorite Songs" value={musicInfo.favoriteSongs} />
              )}
              {musicInfo.liveMusicians && (
                <InfoItem label="Live Musicians" value={musicInfo.liveMusicians} />
              )}
              <InfoItem label="Recorded Music Acceptable" value={musicInfo.recordedMusic} />
              {musicInfo.avoidMusic && (
                <InfoItem label="Music to Avoid" value={musicInfo.avoidMusic} />
              )}
              {musicInfo.culturalMusic && (
                <InfoItem label="Cultural Music" value={musicInfo.culturalMusic} />
              )}
              {musicInfo.specialRequests && (
                <InfoItem label="Special Requests" value={musicInfo.specialRequests} />
              )}
              {musicInfo.notes && (
                <InfoItem label="Additional Notes" value={musicInfo.notes} />
              )}

              <View style={styles.buttonContainer}>
                <TouchableOpacity
                  style={[styles.button, styles.backToListButton]}
                  onPress={handleBack}
                >
                  <Text style={styles.buttonText}>Back</Text>
                </TouchableOpacity>

                {!viewOnly && (
                  <>
                    <TouchableOpacity
                      style={[styles.button, styles.deleteButton]}
                      onPress={handleDelete}
                    >
                      <Text style={styles.buttonText}>Delete</Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={[styles.button, styles.editButton]}
                      onPress={handleEdit}
                    >
                      <Text style={styles.buttonText}>Edit</Text>
                    </TouchableOpacity>
                  </>
                )}
              </View>
            </>
          ) : (
            <View style={styles.noDataContainer}>
              <Text style={styles.noDataText}>No music preferences found.</Text>
              {!viewOnly && (
                <TouchableOpacity
                  style={[styles.button, styles.editButton, { marginTop: 20 }]}
                  onPress={handleEdit}
                >
                  <Text style={styles.buttonText}>Add Music Preferences</Text>
                </TouchableOpacity>
              )}
            </View>
          )}
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0', // Teal header background
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
  },
  notificationIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: '600',
    color: '#FFFFFF', // White title on teal background
    textAlign: 'center',
    marginVertical: ms(20),
  },
  whiteContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF', // White content area
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(20),
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  infoItem: {
    marginBottom: ms(20),
  },
  label: {
    fontSize: ms(16, 0.3),
    color: '#6B7280', // Gray label text
    marginBottom: ms(8),
  },
  value: {
    fontSize: ms(18, 0.3),
    color: '#1C2C4F', // Dark blue value text
    marginBottom: ms(8),
  },
  separator: {
    height: s(2),
    backgroundColor: '#29BFC0', // Teal separator line
    marginTop: ms(8),
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: ms(20),
    paddingBottom: ms(130),
    gap: ms(10),
  },
  button: {
    flex: 1,
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  backToListButton: {
    backgroundColor: '#29BFC0', // Teal back button
  },
  deleteButton: {
    backgroundColor: '#FF4444', // Red delete button
  },
  editButton: {
    backgroundColor: '#FF9F5A', // Orange edit button
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: ms(40),
  },
  loadingText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
  },
  noDataContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: ms(40),
  },
  noDataText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
    textAlign: 'center',
  },
  viewOnlyBanner: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    marginHorizontal: ms(20),
    borderRadius: ms(8),
    marginBottom: ms(10),
  },
  viewOnlyText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
});

export default MusicDataScreen;
